import React from 'react';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import type { SxProps, Theme } from '@mui/material/styles';
import { aliasColors, brandColors, palette } from '../../../theme';

export const OTP_DEFAULT_LENGTH = 6;

export type OtpInputColors = {
  field: string;
  active: string;
  text: string;
  error: string;
};

export type OtpInputProps = {
  /** One entry per box, '' for an empty box. Its length sets the number of boxes. */
  value: string[];
  onChange: (value: string[]) => void;
  /** Red borders and a short shake, e.g. after a wrong code. */
  error?: boolean;
  disabled?: boolean;
  /** Focus the first box on mount. */
  autoFocus?: boolean;
  /** Change it to move focus back to the first box (e.g. after a new code is sent). */
  focusKey?: number | string;
  /** id of the visible label for the group. */
  labelledBy?: string;
  boxHeight?: number;
  gap?: number;
  radius?: number;
  borderWidth?: number;
  /** Typography for the digits. */
  digitSx?: SxProps<Theme>;
  colors?: Partial<OtpInputColors>;
};

type OtpInputState = {
  active: number;
};

const DEFAULT_COLORS: OtpInputColors = {
  field: aliasColors.background.bg03,
  active: brandColors.primary.yovoRed400,
  text: brandColors.neutrals.base1000,
  error: palette.error.main,
};

/** Empty value for `length` boxes. */
export function emptyOtp(length: number = OTP_DEFAULT_LENGTH) {
  return Array.from({ length }, () => '');
}

/** The code as one string, e.g. for the verify call. */
export function otpCode(value: string[]) {
  return value.join('');
}

export function isOtpComplete(value: string[]) {
  return value.length > 0 && value.every((digit) => digit !== '');
}

/**
 * One-time-code boxes. Digits only, one per box; typing moves to the next box,
 * Backspace on an empty box clears the one before it, and a paste fills the
 * boxes from the one it lands in (anything past the last box is dropped).
 */
class OtpInput extends React.Component<OtpInputProps, OtpInputState> {
  state: OtpInputState = { active: 0 };

  private inputs: Array<HTMLInputElement | null> = [];

  componentDidMount() {
    if (this.props.autoFocus !== false) this.focusBox(0);
  }

  componentDidUpdate(prevProps: OtpInputProps) {
    if (prevProps.focusKey !== this.props.focusKey) {
      this.setState({ active: 0 }, () => this.focusBox(0));
    }
  }

  focusBox = (index: number) => {
    this.inputs[index]?.focus();
  };

  applyDigits = (start: number, raw: string) => {
    const value = this.props.value.slice();
    let cursor = start;
    for (const char of raw) {
      if (cursor >= value.length) break;
      value[cursor] = char;
      cursor += 1;
    }
    const active = Math.min(Math.max(cursor, start), value.length - 1);
    this.props.onChange(value);
    this.setState({ active }, () => this.focusBox(active));
  };

  handleDigit = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.replace(/\D/g, '');
    if (!raw) {
      const value = this.props.value.slice();
      value[index] = '';
      this.props.onChange(value);
      this.setState({ active: index });
      return;
    }
    this.applyDigits(index, raw);
  };

  handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    const last = this.props.value.length - 1;
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusBox(index - 1);
      return;
    }
    if (event.key === 'ArrowRight' && index < last) {
      event.preventDefault();
      this.focusBox(index + 1);
      return;
    }
    if (event.key !== 'Backspace' || this.props.value[index] || index === 0) return;
    event.preventDefault();
    const value = this.props.value.slice();
    value[index - 1] = '';
    this.props.onChange(value);
    this.setState({ active: index - 1 }, () => this.focusBox(index - 1));
  };

  handlePaste = (index: number) => (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const raw = (event.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (!raw) return;
    this.applyDigits(index, raw);
  };

  handleFocus = (index: number) => (event: React.FocusEvent<HTMLInputElement>) => {
    this.setState({ active: index });
    event.target.select();
  };

  render() {
    const {
      value,
      error = false,
      disabled = false,
      labelledBy,
      boxHeight = 56,
      gap = 10,
      radius = 12,
      borderWidth = 1.5,
      digitSx,
    } = this.props;
    const colors = { ...DEFAULT_COLORS, ...this.props.colors };

    return (
      <Box
        role="group"
        aria-labelledby={labelledBy}
        data-error={error ? 'true' : 'false'}
        sx={{
          display: 'flex',
          gap: `${gap}px`,
          animation: error ? 'otpInputShake 0.35s' : 'none',
          '@keyframes otpInputShake': {
            '0%, 100%': { transform: 'translateX(0)' },
            '25%': { transform: 'translateX(-6px)' },
            '75%': { transform: 'translateX(6px)' },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        {value.map((digit, index) => {
          const active = index === this.state.active;
          const borderColor = error ? colors.error : active ? colors.active : 'transparent';
          return (
            <Box
              key={index}
              data-active={active ? 'true' : 'false'}
              sx={{
                flex: 1,
                minWidth: 0,
                height: boxHeight,
                boxSizing: 'border-box',
                borderRadius: `${radius}px`,
                backgroundColor: colors.field,
                border: `${borderWidth}px solid ${borderColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: disabled ? 0.6 : 1,
                transition: 'border-color 150ms ease',
              }}
            >
              <InputBase
                value={digit}
                disabled={disabled}
                onChange={this.handleDigit(index)}
                onKeyDown={this.handleKeyDown(index)}
                onPaste={this.handlePaste(index)}
                onFocus={this.handleFocus(index)}
                inputRef={(node: HTMLInputElement | null) => {
                  this.inputs[index] = node;
                }}
                inputProps={{
                  inputMode: 'numeric',
                  autoComplete: index === 0 ? 'one-time-code' : 'off',
                  'aria-label': `Digit ${index + 1}`,
                  'aria-invalid': error || undefined,
                }}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': {
                    p: 0,
                    textAlign: 'center',
                    caretColor: colors.text,
                    color: colors.text,
                    ...(digitSx as object),
                  },
                  '& .MuiInputBase-input:focus': { outline: 'none' },
                }}
              />
            </Box>
          );
        })}
      </Box>
    );
  }
}

export default OtpInput;
